import { Box, Button, Icon, Skeleton, Stack, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaPlus } from "react-icons/fa";
import { useGetProductGroupsForSourcingEventQuery } from "../../../../../../autogen/bff-api";
import { ProductFileUploader } from "../../../../../../common/products/ProductFileUploader";
import { ProductGroupForm } from "../../../../../../common/products/ProductGroupForm";
import { ProductTemplateModal } from "../../../../../../common/products/ProductTemplateModal";
import { useAppDispatch, useAppSelector } from "../../../../../../common/redux/hooks";
import { mapDtoToProductGroup, setProductGroups } from "../../../../../../common/redux/reducers/productReducer";
import { StepNavigation } from "../../../../../../common/StepNavigation";
import { ProductGroup } from "../../../../../../common/types";

interface Props {
  eventId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const ProductsStep = ({ eventId, previousStep, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { data, isLoading, error } = useGetProductGroupsForSourcingEventQuery({ sourcingEventId: eventId });
  const productGroups = useAppSelector((state) => state.product.data.productGroups);

  useEffect(() => {
    if ((!productGroups.length || productGroups[0]?.sourcingEventId !== eventId) && data?.productGroups.length) {
      dispatch(setProductGroups(data?.productGroups.map((group) => mapDtoToProductGroup(group))));
    }
    if (productGroups.length && !data?.productGroups.length) {
      dispatch(setProductGroups([]));
    }
    if (productGroups && productGroups[0]?.sourcingEventId !== eventId) {
      dispatch(setProductGroups(undefined));
    }
  }, [data, dispatch, eventId, productGroups]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [productGroupDraft, setProductGroupDraft] = useState<ProductGroup>();

  if (isLoading) {
    return (
      <Stack spacing="4">
        <Skeleton height="8" width="52" rounded="md" />
        <Skeleton height="32" rounded="md" />
        <Skeleton height="32" rounded="md" />
      </Stack>
    );
  }

  if (error || !data) return <Text>{t("Something went wrong")}...</Text>;

  return (
    <Box width="full" maxWidth="full" pt="10" pb="8">
      <ProductFileUploader
        showExplanation={productGroups.length === 0}
        setProductGroupDraft={setProductGroupDraft}
        setIsModalOpen={setIsModalOpen}
        sourcingEventId={eventId}
      />
      <Button variant="outline" mr="5px" colorScheme="teal" size="sm" onClick={() => setIsModalOpen(true)}>
        {t("New product group")} <Icon as={FaPlus} ml="2" />
      </Button>
      <Box maxWidth="full" py="8">
        {productGroups?.map((group) => (
          <Box key={group.id} maxWidth="full" pb="5">
            <ProductGroupForm group={group} />
          </Box>
        ))}
      </Box>
      <ProductTemplateModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        productGroupDraft={productGroupDraft}
        sourcingEventId={eventId}
      />
      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
